import { computed, action, observable } from 'mobx';
import { ProductSelectedUseCase } from '../use-cases/ProductSelectedUseCase';
import { ProductBaseController } from './ProductBaseController';
import { IFreeShippingAndReturnConfig } from '../interfaces/ITranslations';
import { ComponentTypes } from '../constants/ComponentTypes';
import { IProductQuickviewConfig } from '../interfaces/IProductQuickview';
import { getConfigOverrides } from '../utils/ConfigUtils';
import { getMobileCarouselSettings } from '../constants/ImageThumbnailCarouselSettings';

export class ProductQuickViewController extends ProductBaseController {
    @observable public isClickable: boolean = true;
    @observable public showOverlay: boolean = true;
    @observable public selectedRating: number = -1;
    @observable public isLoaded: boolean = false;
    @observable public positionIndex: number = -1;
    protected config: IProductQuickviewConfig;

    public initialize = (config: IProductQuickviewConfig) => {
        this.config = config;
        const { imageCarouselSettings } = config;
        const carouselSettings =
            imageCarouselSettings && imageCarouselSettings[ComponentTypes.PRODUCT_QUICKSHOP];

        this.setMediaSettings(carouselSettings);
        this.isLoaded = true;
    };

    @computed
    public get freeShippingAndReturnConfig() {
        const { overrides, ...freeShippingAndReturnConfig } = this.config
            .freeShippingAndReturn as IFreeShippingAndReturnConfig;

        const configOverrides = overrides && overrides[ComponentTypes.PRODUCT_QUICKSHOP];

        return { ...freeShippingAndReturnConfig, ...configOverrides };
    }

    @computed
    public get loyaltyLearnMoreConfig() {
        return getConfigOverrides(this.config.loyaltyLearnMore, ComponentTypes.PRODUCT_QUICKSHOP);
    }

    @computed
    get data() {
        const {
            product,
            selectedSku,
            productQuickViewThumbnailsCarouselSettings,
            isThumbnailsCarouselEnabled,
            isShortDescriptionEnabled,
            loyaltyLearnMoreConfig,
            isResponsiveThumbnailsCarouselEnabled
        } = this;

        return {
            ...this.baseData,
            sizes: product.sizes,
            shades: product.shades,
            quantity: product.quantity,
            autoReplenishOptions: [],
            selectedAutoReplenishOption: this.selectedAutoReplenishOption,
            isLoaded: this.isLoaded,
            selectedSku,
            productQuickViewThumbnailsCarouselSettings,
            isThumbnailsCarouselEnabled,
            isResponsiveThumbnailsCarouselEnabled,
            freeShippingAndReturnConfig:
                this.config.freeShippingAndReturn && this.freeShippingAndReturnConfig,
            isShortDescriptionEnabled,
            loyaltyLearnMoreConfig
        };
    }

    @computed
    get defaultSku() {
        return this.product.defaultSku;
    }

    @computed
    private get productQuickViewImageCarouselSettings() {
        return this.imageCarouselSettings && this.imageCarouselSettings.productQuickshop;
    }

    @computed
    private get productQuickViewThumbnailsCarouselSettings() {
        return (
            this.productQuickViewImageCarouselSettings &&
            this.productQuickViewImageCarouselSettings.thumbnailsCarouselSettings
        );
    }

    @computed
    private get isThumbnailsCarouselEnabled() {
        return (
            this.productQuickViewThumbnailsCarouselSettings &&
            this.productQuickViewThumbnailsCarouselSettings.enabled
        );
    }

    @computed
    public get isResponsiveThumbnailsCarouselEnabled() {
        const responsiveCarouselSettings = getMobileCarouselSettings(
            this.productQuickViewThumbnailsCarouselSettings
        );

        return responsiveCarouselSettings
            ? responsiveCarouselSettings && responsiveCarouselSettings.enabled
            : false;
    }

    @computed
    private get productQuickshopShortDescriptionSettings() {
        return this.shortDescriptionSettings &&
            this.shortDescriptionSettings.overrides &&
            this.shortDescriptionSettings.overrides[ComponentTypes.PRODUCT_QUICKSHOP]
            ? {
                  ...this.shortDescriptionSettings,
                  ...this.shortDescriptionSettings.overrides[ComponentTypes.PRODUCT_QUICKSHOP]
              }
            : this.shortDescriptionSettings;
    }

    @computed
    private get isShortDescriptionEnabled() {
        const shortDescriptionSettings = this.productQuickshopShortDescriptionSettings;

        return shortDescriptionSettings && shortDescriptionSettings.enabled;
    }

    // @todo ADD EVENT EMITER TO THE ECOMAPP TO CHANGE ROUTE
    public goToSPP = async (positionIndex: number) => {
        const product = this.product;
        const navigateToProductUseCase = new ProductSelectedUseCase(
            this.data.productUrl || '',
            this.selectedSkuId,
            positionIndex,
            product
        );
        await navigateToProductUseCase.execute();
    };

    @action
    public disableEdit = () => {
        this.isClickable = false;
    };

    @action
    public toggleOverlay = () => {
        if (this.isClickable) {
            this.showOverlay = !this.showOverlay;
        }
    };

    @action
    public onReadReviewsClick = () => {
        return;
    };
}
